/**
 * Quick Add Mini Modal - Mobile simplified add-to-cart modal
 *
 * Lightweight modal for mobile devices with size selection only.
 */

if (!customElements.get('quick-add-mini-modal')) {
  customElements.define('quick-add-mini-modal', class QuickAddMiniModal extends ModalDialog {
    constructor() {
      super();
    }

    async show(opener) {
      // Chamar método pai
      super.show(opener);

      // Passar informação de cor pré-selecionada do modal-opener para o form
      const form = this.querySelector('quick-add-mini-form');
      if (form) {
        // Verificar se modal-opener tem cor pré-selecionada (quando usuário clica em swatch)
        const modalOpener = opener?.closest('modal-opener');
        if (modalOpener?.dataset?.preselectedColor) {
          const preselectedColor = modalOpener.dataset.preselectedColor;
          form.dataset.currentColor = preselectedColor;

          // Atualizar tamanhos disponíveis para esta cor
          if (typeof form.updateAvailableSizes === 'function') {
            await form.updateAvailableSizes(preselectedColor);
          }

          // Atualizar imagem do modal para esta cor
          if (typeof form.updateModalImage === 'function') {
            await form.updateModalImage(preselectedColor);
          }
        }

        // Resetar seleção de tamanho ao abrir
        if (typeof form.reset === 'function') {
          form.reset();
        }
      }
    }

    hide(preventFocus = false) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);

      if (preventFocus) this.openedBy = null;
      super.hide();
    }
  });
}

if (!customElements.get('quick-add-mini-form')) {
  customElements.define('quick-add-mini-form', class QuickAddMiniForm extends HTMLElement {
    constructor() {
      super();

      this.submitButton = this.querySelector('.quick-add-mini-modal__submit');
      // Buscar todos os radio inputs de tamanho (suporta múltiplos padrões de nome)
      this.sizeInputs = this.querySelectorAll('.product-form__controls-group input[type="radio"]');

      this.init();
    }

    init() {
      // Event listener para submit
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
      }

      // Event listener para mudança de tamanho
      this.sizeInputs.forEach(input => {
        input.addEventListener('change', this.updateSubmitButton.bind(this));
      });

      // Event delegation para capturar clicks em labels
      const sizesContainer = this.querySelector('.product-form__controls-group');
      if (sizesContainer) {
        sizesContainer.addEventListener('click', (e) => {
          // Verificar se clicou no label ou dentro dele
          const label = e.target.closest('.pills-variant-labels') || e.target.closest('label');

          if (label) {
            let input = null;

            // Método 1: Tentar via htmlFor (se label tem atributo 'for')
            if (label.htmlFor) {
              input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
            }

            // Método 2: Tentar via previousElementSibling (se input está antes do label)
            if (!input && label.previousElementSibling?.type === 'radio') {
              input = label.previousElementSibling;
            }

            // Método 3: Tentar via querySelector dentro do label (se input está dentro do label)
            if (!input) {
              input = label.querySelector('input[type="radio"]');
            }

            // Se encontrou o input e ele está habilitado, marcar e disparar evento
            if (input && input.type === 'radio' && !input.disabled && !input.classList.contains('disabled')) {
              input.checked = true;

              // IMPORTANTE: Disparar evento 'change' para notificar outros listeners
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }

      // Atualizar estado inicial do botão
      this.updateSubmitButton();
    }

    /**
     * Reseta o formulário
     */
    reset() {
      // Desmarcar todos os tamanhos
      this.sizeInputs.forEach(input => {
        input.checked = false;
      });

      // Atualizar botão
      this.updateSubmitButton();
    }

    /**
     * Atualiza tamanhos disponíveis baseado na cor selecionada
     */
    async updateAvailableSizes(color) {
      const productHandle = this.dataset.productHandle;

      try {
        // Fetch product data (usa cache se já foi carregado)
        const product = await ShopifyAPI.Product.get(productHandle);

        // Encontrar tamanhos disponíveis para esta cor
        const availableSizes = new Set();
        product.variants.forEach(variant => {
          // Verificar se variant tem esta cor
          const matchesColor = variant.option1 === color ||
                              variant.option2 === color ||
                              variant.option3 === color;

          if (matchesColor && variant.available) {
            // Extrair o tamanho (opção que não é cor)
            const size = [variant.option1, variant.option2, variant.option3]
              .find(opt => opt !== color);
            if (size) availableSizes.add(size);
          }
        });

        // Atualizar inputs: enable disponíveis, disable indisponíveis
        this.sizeInputs.forEach(input => {
          const size = input.value;
          const isAvailable = availableSizes.has(size);

          input.disabled = !isAvailable;
          input.classList.toggle('disabled', !isAvailable);

          // Desmarcar se ficou indisponível
          if (!isAvailable && input.checked) {
            input.checked = false;
          }
        });

        // Selecionar primeiro disponível
        if (!this.querySelector('input[name^="mini-size-"]:checked')) {
          const firstAvailable = this.querySelector('input[name^="mini-size-"]:not(:disabled)');
          if (firstAvailable) {
            firstAvailable.checked = true;
          }
        }

        this.updateSubmitButton();

      } catch (error) {
        console.error('[QuickAddMiniForm] Error updating sizes:', error);
      }
    }

    /**
     * Atualiza imagem do modal baseado na cor selecionada
     */
    async updateModalImage(color) {
      const productHandle = this.dataset.productHandle;

      try {
        const product = await ShopifyAPI.Product.get(productHandle);

        // Encontrar variant que corresponde a essa cor
        const variantForColor = product.variants.find(v => {
          return v.option1 === color || v.option2 === color || v.option3 === color;
        });

        if (variantForColor && variantForColor.featured_image) {
          const modalImage = this.querySelector('.quick-add-mini-modal__image img');
          if (modalImage) {
            // Usar diretamente a URL da imagem do Shopify
            const imageUrl = variantForColor.featured_image.src;

            // Atualizar imagem (Shopify já retorna URLs otimizadas)
            modalImage.src = imageUrl;
            modalImage.srcset = '';
            modalImage.alt = variantForColor.featured_image.alt || product.title;
          }
        }
      } catch (error) {
        console.error('[QuickAddMiniForm] Error updating image:', error);
      }
    }

    /**
     * Atualiza estado do botão "Adicionar"
     */
    updateSubmitButton() {
      const selectedSize = this.querySelector('input[name^="mini-size-"]:checked');

      if (this.submitButton) {
        if (selectedSize) {
          this.submitButton.disabled = false;
        } else {
          this.submitButton.disabled = true;
        }
      }
    }

    /**
     * Handle form submit - adiciona ao carrinho
     */
    async handleSubmit(e) {
      e.preventDefault();

      const selectedSize = this.querySelector('input[name^="mini-size-"]:checked');
      if (!selectedSize) {
        console.warn('[QuickAddMiniForm] No size selected');
        return;
      }

      const productHandle = this.dataset.productHandle;
      const currentColor = this.dataset.currentColor;
      const selectedSizeValue = selectedSize.value;

      // Encontrar a variante correspondente
      const variantId = await this.findVariantId(productHandle, currentColor, selectedSizeValue);

      if (!variantId) {
        console.error('[QuickAddMiniForm] Variant not found');
        this.showError('Variante não encontrada');
        return;
      }

      // Adicionar ao carrinho
      await this.addToCart(variantId);
    }

    /**
     * Busca o ID da variante baseado em cor + tamanho
     */
    async findVariantId(productHandle, color, size) {
      if (!productHandle) {
        console.error('[QuickAddMiniForm] Missing product handle');
        return null;
      }

      try {
        const product = await ShopifyAPI.Product.get(productHandle);

        const variant = product.variants.find(v => {
          const matchesSize = v.option1 === size || v.option2 === size || v.option3 === size;
          const isAvailable = v.available;

          // Se não há cor especificada (produtos com apenas uma opção de tamanho)
          if (!color || color === '') {
            return matchesSize && isAvailable;
          }

          // Se há cor, validar ambos
          const matchesColor = v.option1 === color || v.option2 === color || v.option3 === color;
          return matchesColor && matchesSize && isAvailable;
        });

        if (variant) {
          return variant.id;
        } else {
          console.warn('[QuickAddMiniForm] No matching variant found');
          return null;
        }
      } catch (error) {
        console.error('[QuickAddMiniForm] Error fetching product:', error);
        return null;
      }
    }

    /**
     * Adiciona item ao carrinho via ShopifyAPI
     */
    async addToCart(variantId) {
      // Mostrar loading
      this.setLoading(true);

      try {
        await ShopifyAPI.Cart.add({
          id: variantId,
          quantity: 1
        });

        console.log('[QuickAddMiniForm] Added to cart successfully');

        // Fechar modal
        const modal = this.closest('quick-add-mini-modal');
        if (modal && typeof modal.hide === 'function') {
          modal.hide();
        }

        // Buscar cart drawer atualizado e abrir
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          try {
            // Fetch sections atualizadas do Shopify
            const response = await fetch(`${window.routes?.cart_url || '/cart'}?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-mobile`);
            const sectionsData = await response.json();

            // Shopify retorna sections diretamente, mas renderContents() espera { sections: {...} }
            const data = { sections: sectionsData };

            // Re-renderizar drawer com HTML atualizado (inclui título com count correto)
            if (typeof cartDrawer.renderContents === 'function' && data.sections) {
              cartDrawer.renderContents(data);
            } else {
              // Fallback: apenas abrir drawer
              setTimeout(() => {
                if (typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                }
              }, 300);
            }
          } catch (error) {
            console.error('[QuickAddMiniForm] Error updating drawer:', error);
            // Fallback: abrir drawer mesmo com erro
            setTimeout(() => {
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }, 300);
          }
        }

        // Publicar evento para atualizar outros listeners (CartCounterUpdater)
        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add-mini' });
        }

      } catch (error) {
        console.error('[QuickAddMiniForm] Error adding to cart:', error);
        this.showError(error.message || 'Erro ao adicionar ao carrinho');
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Controla estado de loading
     */
    setLoading(isLoading) {
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;

        if (isLoading) {
          this.submitButton.textContent = 'Adicionando...';
        } else {
          this.submitButton.textContent = 'Adicionar ao carrinho';
        }
      }
    }

    /**
     * Mostra mensagem de erro
     */
    showError(message) {
      const errorContainer = this.querySelector('.quick-add-mini-modal__error-text');
      if (errorContainer) {
        errorContainer.textContent = message;

        // Auto-hide after 5 seconds
        setTimeout(() => {
          errorContainer.textContent = '';
        }, 5000);
      }
      console.error('[QuickAddMiniForm] Error:', message);
    }
  });
}
